import React, { useContext, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router";
import { Box, Typography } from "@material-ui/core";
import {
  IconName,
  ActionButton,
  GenericAddOnItem as AddOnItem,
  ErrorOutsideComponentType,
  getTotalPriceText,
  getRewardsString,
  twoDecimalFormatter,
  CorpPolicyBanner,
  SanitizedHtml,
} from "halifax";
import {
  FiatPrice,
  RewardsPrice,
  CFAR_HEADER,
  CAP1_HOTEL_CFAR_ADD_ON_CHOICE,
  Prices,
  CallState,
} from "redmond";
import clsx from "clsx";
import { useShowPolicyBanner } from "@capone/common";
import { useExperimentIsVariant } from "@capone/experiments";
import { PriceBreakdown } from "../../../../book/components";
import { goToCheckout } from "../../../../shop/utils/queryStringHelpers";
import { trackEvent } from "../../../../../api/v0/analytics/trackEvent";
import { DO_NOT_APPLY_OPTION_KEY } from "../../../reducer";
import { AddOnCustomizeConnectorProps } from "./container";
import * as constants from "./constants";
import "./styles.scss";
import { ClientContext } from "../../../../../App";
import {
  StandAloneCfarDetails,
  CfarOption,
} from "../cfar/StandAloneCfarDetails";
import { Cap1HotelCfarHtsMerchandising } from "../cfar/Cap1HotelCfarHtsMerchandising";
import { isHtsCfarOffer } from "../utils";

export interface IAddOnCustomizeProps
  extends AddOnCustomizeConnectorProps,
    RouteComponentProps {
  // note: this callback function is used to overwrite the default (successful) on-continue behaviour
  onContinueSuccess?: () => void;
  hidePricingBreakdown?: boolean;
  disableCollapse?: boolean;
}

enum AncillaryType {
  Cfar,
}

export const AddOnCustomize = (props: IAddOnCustomizeProps) => {
  const {
    history,
    isCfarAvailable,
    selectedCfarId,
    cfarQuoteV2,
    cancellationPolicy,
    isOptionSelectionComplete,
    rewardsKey,
    refundableRoomProductCorrespondingToChosenRoom,
    hidePricingBreakdown,
    disableCollapse,
    onContinueSuccess = (shouldAddCfarOfferParam: boolean) => {
      goToCheckout({ history, shouldAddCfarOfferParam });
    },
    corporateTravel,
  } = props;
  const [isCfarCollapsed, setIsCfarCollapsed] = useState<boolean>(false);
  const [cfarError, setCfarError] = useState<ErrorOutsideComponentType>();
  const cfarOfferV2Prices = cfarQuoteV2?.premiumAmount;

  const cfarOfferPricesToUse = cfarOfferV2Prices as Prices;
  const headerStyle = "default";
  const isContinueCheckoutTextEnable = useExperimentIsVariant(
    "c1-fintech-cfar-hotel-continue-checkout-text-enable",
    "available"
  );

  const handleOnContinue = () => {
    if (isOptionSelectionComplete) {
      onContinueSuccess(isHtsCfarOffer(cfarQuoteV2));
    } else if (!selectedCfarId) {
      setCfarError(ErrorOutsideComponentType.NoOptionSelected);
    }
  };

  const getFormattedTotalPriceText = (fiat: FiatPrice | undefined) => {
    if (fiat) {
      return getTotalPriceText({
        price: fiat,
        priceFormatter: twoDecimalFormatter,
      });
    }
    return "";
  };

  const getFormattedRewardsText = (
    rewardsPrices: { [key: string]: RewardsPrice } | undefined | null,
    selectedRewardsAccountId: string | null | undefined
  ) => {
    if (rewardsPrices && selectedRewardsAccountId) {
      const rewards = rewardsPrices[selectedRewardsAccountId];
      return getRewardsString(rewards);
    }
    return "";
  };

  const isAncillaryDisplayed = (type: AncillaryType): boolean => {
    switch (type) {
      case AncillaryType.Cfar:
        return isCfarAvailable;
      default:
        return false;
    }
  };

  // I'll admit this is kind of a mess... but should be cleaner once we roll out CFAR V2
  const renderCfarAddOnItem = () => {
    if (!cfarQuoteV2) {
      return null;
    }
    if (isHtsCfarOffer(cfarQuoteV2)) {
      return <Cap1HotelCfarHtsMerchandising cfarRateQuote={cfarQuoteV2} />;
    }

    const content = (
      <StandAloneCfarDetails
        contentOnly
        cardContentProps={{
          hideTopContent: true,
          hideSubtitle: true,
          hideContinueCta: true,
          disablePartialScroll: true,
          readTermsAndConditionsLinkPosition: "left",
          onSelectValueCallback: (option: CfarOption) => {
            trackEvent({
              eventName: CAP1_HOTEL_CFAR_ADD_ON_CHOICE,
              properties: {
                ...cfarQuoteV2.trackingProperties.properties,
                cfar_choice: option === CfarOption.Yes ? 1 : 0,
                cfar_rr_hotel_choice:
                  refundableRoomProductCorrespondingToChosenRoom === undefined
                    ? "none"
                    : 0,
                cfar_fee_is_refundable: cfarQuoteV2.refundPremiumAtExercise,
              },
              ...(cfarQuoteV2.trackingProperties.encryptedProperties
                ? {
                    encryptedProperties: [
                      cfarQuoteV2.trackingProperties.encryptedProperties,
                    ],
                  }
                : {}),
            });
            setCfarError(undefined);
          },
          onConfirmSelectedValueCallback: () => {
            setTimeout(() => {
              setIsCfarCollapsed(true);
            }, 800);
          },
          error: cfarError,
        }}
        disabled={false}
        currentHotelCfarQuote={cfarQuoteV2}
        currentCancellationPolicy={cancellationPolicy}
      />
    );

    return (
      <AddOnItem
        key="cfar"
        type="cancel-for-any-reason"
        header={
          <SanitizedHtml
            as={Typography}
            variant="inherit"
            html={CFAR_HEADER({ textVersion: "short" })}
          />
        }
        headerStyle={headerStyle}
        subtitle={constants.CFAR_SUBTITLE_COPY}
        iconName={IconName.CheckShieldNoCircle}
        content={content}
        fetchAncillaryOfferCallState={CallState.Success}
        error={cfarError}
        isCollapsed={isCfarCollapsed}
        setIsCollapsed={setIsCfarCollapsed}
        hasSelectedOption={!!selectedCfarId}
        priceCopy={
          selectedCfarId?.value !== DO_NOT_APPLY_OPTION_KEY &&
          cfarOfferPricesToUse
            ? constants.getPriceAndRewardsCopy({
                price: getFormattedTotalPriceText(cfarOfferPricesToUse.fiat),
                rewards: getFormattedRewardsText(
                  cfarOfferPricesToUse.rewards,
                  rewardsKey
                ),
              })
            : undefined
        }
        disableCollapse={disableCollapse}
        disabled={false}
      />
    );
  };

  const availableAncillariesList: AncillaryType[] = [
    isCfarAvailable ? [AncillaryType.Cfar] : [],
  ].flat();
  const addOnItems: (JSX.Element | null)[] = availableAncillariesList.map(
    (ancillaryType) => {
      if (!isAncillaryDisplayed(ancillaryType)) {
        return null;
      }

      switch (ancillaryType) {
        case AncillaryType.Cfar:
          return renderCfarAddOnItem();
        default:
          return null;
      }
    }
  );

  const isContinueButtonDisabled = useMemo(
    () => isContinueCheckoutTextEnable && !isOptionSelectionComplete,
    [isOptionSelectionComplete, isContinueCheckoutTextEnable]
  );

  const { policies } = useContext(ClientContext);
  const showPolicyBanner = useShowPolicyBanner(policies);

  return (
    <Box className="hotel-add-on-customize-root">
      <Box className={clsx("hotel-add-on-customize-container")}>
        <Box className="add-on-items-section">{addOnItems}</Box>
        {!hidePricingBreakdown && (
          <Box className="checkout-breakdown-section-container">
            <CorpPolicyBanner
              variant="base"
              corporateTravel={showPolicyBanner ? corporateTravel : undefined}
              productType="hotel"
            />
            <Box className="checkout-breakdown-section">
              <Typography className="header-copy" variant="h2">
                {constants.CHECKOUT_BREAKDOWN}
              </Typography>
              <Box className="price-breakdown-wrapper">
                <PriceBreakdown />
              </Box>
              <ActionButton
                disabled={isContinueButtonDisabled}
                className="continue-button"
                message={constants.CONTINUE_TO_CHECKOUT_COPY}
                onClick={handleOnContinue}
              />
              {isContinueButtonDisabled && (
                <Typography className="continue-option-text" variant="body2">
                  Please accept or decline the options in order to proceed
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
